import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  numberOfDependentsQuestions,
  additionalWithholdingQuestions9_20,
  additionalWithholdingQuestions9_22,
  additionalWithholdingQuestions8_23,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

const getAdditionalWithholdingQuestions = (version = '') => {
  switch (version) {
    case '9_20':
      return additionalWithholdingQuestions9_20;
    case '9_22':
      return additionalWithholdingQuestions9_22;
    default:
      return additionalWithholdingQuestions8_23;
  }
};

function IndianaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);
  prefilledData.employee_name = formatFullName();

  const step1InitialQuestions = getInitialQuestions(basicInfoQuestions);
  const [stepOneBasicQuestions, setStepOneBasicQuestions] = useState(
    step1InitialQuestions,
  );

  const step1DependentsQuestions = getInitialQuestions(
    numberOfDependentsQuestions,
  );
  const [stepOneDependentsQuestions, setStepOneDependentsQuestions] = useState(
    step1DependentsQuestions,
  );
  const [countiesEffectiveNextYear, setCountiesEffectiveNextYear] = useState(
    data.counties_effective_next_year || false,
  );
  const [claimSelfExemption, setClaimSelfExemption] = useState(
    data.claim_self_exemption || false,
  );
  const [claimSpouseExemption, setClaimSpouseExemption] = useState(
    data.claim_spouse_exemption || false,
  );
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || false,
  );
  const [numberOfDependents, setNumberOfDependents] = useState(
    data.number_of_dependents || 0,
  );

  const [selfAbove65, setSelfAbove65] = useState(data.self_above_65 || false);
  const [selfBlind, setSelfBlind] = useState(data.self_blind || false);
  const [spouseAbove65, setSpouseAbove65] = useState(
    data.spouse_above_65 || false,
  );
  const [spouseBlind, setSpouseBlind] = useState(data.spouse_blind || false);

  const additionalWithholdingQuestions = getAdditionalWithholdingQuestions(version);
  const step1AdditionalWitholdingQuestions = getInitialQuestions(
    additionalWithholdingQuestions,
  );
  const [
    stepOneAdditionalWitholdingQuestions,
    setStepOneAdditionalWitholdingQuestions,
  ] = useState(step1AdditionalWitholdingQuestions);

  useEffect(() => {
    setStepOneAdditionalWitholdingQuestions(step1AdditionalWitholdingQuestions);
  }, [additionalWithholdingQuestions]);

  const step1BasicForm = useRef();
  const step1AdditionalWitholdingForm = useRef();
  const step1DependentsForm = useRef();

  const renderStep1BasicForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneBasicQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneBasicQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneBasicQuestions(questions);
        }}
        ref={step1BasicForm}
      />
    </div>
  );

  const renderAdditionalBasicForm = (isReviewStep) => {
    switch (version) {
      case '8_23':
        return (
          <Flex column mb="20px">
            <S.QuestionWrapper>
              <Flex mb="8px">
                <label>
                  <Flex>
                    <S.Input
                      style={{ height: '20px' }}
                      type="checkbox"
                      id="countiesEffectiveNextYear"
                      value={countiesEffectiveNextYear}
                      onChange={(e) => setCountiesEffectiveNextYear(e.target.checked)}
                      checked={countiesEffectiveNextYear}
                      disabled={isReviewStep}
                    />
                    <Box ml={2} />
                    Check this box if the changes to the counties are effective for the next calendar year
                  </Flex>
                </label>
              </Flex>
            </S.QuestionWrapper>
          </Flex>
        );
      default:
        return null;
    }
  };

  const renderAdditionalWithholdingForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneAdditionalWitholdingQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneAdditionalWitholdingQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneAdditionalWitholdingQuestions(questions);
        }}
        ref={step1AdditionalWitholdingForm}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1BasicForm()}
          {renderAdditionalBasicForm()}
          {renderClaimQuestions()}
          {renderNumberOfDependentsQuestionForm()}
          {renderAdditionalExemptions()}
          {renderAdditionalWithholdingForm()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1BasicForm && step1BasicForm.current) {
              step1BasicForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (
              step1AdditionalWitholdingForm
              && step1AdditionalWitholdingForm.current
            ) {
              step1AdditionalWitholdingForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (step1DependentsForm && step1DependentsForm.current) {
              step1DependentsForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const stepOneBasicData = stepOneBasicQuestions.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              const counties_effective_next_year = countiesEffectiveNextYear; // transform data to match
              const stepOneAdditionalWithholdingData = stepOneAdditionalWitholdingQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const exemptions = {
                claim_self_exemption: claimSelfExemption,
                claim_spouse_exemption: claimSpouseExemption,
              };
              const number_of_dependents = parseInt(
                stepOneDependentsQuestions[0].answer.answer
                  .number_of_dependents,
              ) || 0;
              const additionalExemptions = {
                self_above_65: selfAbove65,
                self_blind: selfBlind,
                spouse_above_65: spouseAbove65,
                spouse_blind: spouseBlind,
              };

              const total_additional_exemptions = _.countBy(additionalExemptions, (v) => v).true || 0;

              let total_exemptions = number_of_dependents + total_additional_exemptions;
              if (claimSelfExemption) total_exemptions += 1;
              if (claimSpouseExemption) total_exemptions += 1;

              const data = {
                ...stepOneBasicData,
                ...exemptions,
                ...stepOneAdditionalWithholdingData,
                ...additionalExemptions,
                counties_effective_next_year,
                number_of_dependents,
                total_additional_exemptions,
                total_exemptions,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1BasicForm(true)}
        {renderAdditionalBasicForm(true)}
        {renderClaimQuestions(true)}
        {renderNumberOfDependentsQuestionForm(true)}
        {renderAdditionalExemptions(true)}
        {renderAdditionalWithholdingForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const renderClaimQuestions = (isReviewStep) => (
    <Flex column mb="20px">
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel>Exemptions</S.QuestionLabel>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimSelfExemption}
                onChange={(e) => setClaimSelfExemption(e.target.checked)}
                checked={claimSelfExemption}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              You are entitled to one exemption for yourself. If you wish to
              claim the exemption, check this box
            </Flex>
          </label>
        </Flex>
        <Flex>
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={claimSpouseExemption}
                onChange={(e) => setClaimSpouseExemption(e.target.checked)}
                checked={claimSpouseExemption}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              If you are married and your spouse does not claim his/her
              exemption, you may claim it by checking this box
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAdditionalExemptions = (isReviewStep) => (
    <Flex column mb="20px">
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel>
            Check the following boxes that apply to you for additional
            exemptions:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={selfAbove65}
                onChange={(e) => setSelfAbove65(e.target.checked)}
                checked={selfAbove65}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              You are 65 or older
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={selfBlind}
                onChange={(e) => setSelfBlind(e.target.checked)}
                checked={selfBlind}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              You are blind
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={spouseAbove65}
                onChange={(e) => setSpouseAbove65(e.target.checked)}
                checked={spouseAbove65}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              Your spouse is 65 or older
            </Flex>
          </label>
        </Flex>
        <Flex mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '20px' }}
                type="checkbox"
                value={spouseBlind}
                onChange={(e) => setSpouseBlind(e.target.checked)}
                checked={spouseBlind}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              Your spouse is blind
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderNumberOfDependentsQuestionForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneDependentsQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneDependentsQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneDependentsQuestions(questions);
        }}
        ref={step1DependentsForm}
      />
    </div>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default IndianaForm;
